import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Drawer, Button, Space, Typography, message, Tooltip } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, AuditOutlined, FormOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConversationSidebar, ChatHistory, ChatInput } from './components';
import { generatePageContextMarkdown } from '../../../utils/pageContextMarkdown';
import { getCurrentMerchantTokenSnapshot } from '@contexts/admin/MerchantContext';
import { formatMessageTime } from './components/utils';

const { Text } = Typography;

/**
 * AssistantDrawer - ChatGPT-like AI Chat Assistant for Operators and Admins
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether drawer is visible
 * @param {Function} props.onClose - Close handler
 */
export default function AssistantDrawer({ visible, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Current conversation state
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);

  // Input state
  const [inputValue, setInputValue] = useState('');
  const [processing, setProcessing] = useState(false);
  const [streamingContent, setStreamingContent] = useState([]); // Array of chunks
  const [currentJobId, setCurrentJobId] = useState(null);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [currentRunId, setCurrentRunId] = useState(null);
  const [activeStreamConvId, setActiveStreamConvId] = useState(null); // which conv owns the active stream (for UI)

  const eventSourceRef = useRef(null);
  const streamingBuffer = useRef('');
  const streamingConversationId = useRef(null); // tracks which conversation owns the active stream (for async guard)
  const currentConversationIdRef = useRef(null); // mirrors currentConversationId state for sync reads in SSE callbacks

  const authHeaders = useCallback((extra = {}) => {
    const headers = { 'Content-Type': 'application/json', ...extra };
    const token = getCurrentMerchantTokenSnapshot();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, []);

  // Fetch conversations list — 帶 exclude_assistant_type=template_assistant，
  // 讓 BE 直接濾掉 OrderTemplate GoBot 的對話，不該出現在通用 AI Assistant sidebar。
  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const response = await fetch('/api/ai/v1/conversations?exclude_assistant_type=template_assistant', {
        method: 'GET',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // Fetch conversation messages
  const fetchConversationMessages = useCallback(async (conversationId) => {
    try {
      const response = await fetch(`/api/ai/v1/conversations/${conversationId}`, {
        method: 'GET',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const messages = data.data?.messages || [];
        setChatHistory(messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content, // Array with types: [{ type: "text", text: "..." }]
          status: msg.status,
          created_at: msg.created_at
        })));
      }
    } catch (error) {
      console.error('Failed to fetch conversation messages:', error);
    }
  }, []);

  // Load conversations when drawer opens
  useEffect(() => {
    if (visible) {
      fetchConversations();

      // Show welcome message if no conversation selected
      if (!currentConversationId && chatHistory.length === 0) {
        setChatHistory([{
          role: 'assistant',
          content: [{ type: 'text', text: t('operator.ai_chat.welcome_message', 'Hello! I\'m your AI assistant. How can I help you today?') }],
          created_at: formatMessageTime(new Date().toISOString())
        }]);
      }
    }
  }, [visible, t, fetchConversations]);

  // Keep currentConversationIdRef in sync so SSE callbacks can read it synchronously
  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // Cleanup EventSource
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Stream AI response when jobId is set
  useEffect(() => {
    if (!currentJobId) return;

    const streamUrl = `/api/ai/v1/streams/${currentJobId}`
    const es = new EventSource(streamUrl);
    eventSourceRef.current = es;

    const cleanup = () => {
      es.close();
      eventSourceRef.current = null;
      setProcessing(false);
      setCurrentJobId(null);
      setCurrentRunId(null);
      setStreamingContent([]);
      setActiveStreamConvId(null);
      streamingBuffer.current = '';
    };

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);

        switch (msg.type) {
          case 'status': {
            const statusContent = msg.data || msg.content || '';
            if (statusContent) {
              setStreamingContent([{ type: 'status', text: statusContent }]);
            }
            break;
          }
          case 'token':
          case 'chunk': {
            const chunkContent = msg.data || msg.content || '';
            if (chunkContent) {
              streamingBuffer.current = streamingBuffer.current + chunkContent;
              setStreamingContent([{ type: 'text', text: streamingBuffer.current }]);
            }
            break;
          }

          case 'done': {
            // Clear streaming state and reload messages from server
            // setStreamingContent([]);
            streamingBuffer.current = '';
            if (msg.content) {
              try {
                const parsed = typeof msg.content === 'string'
                  ? JSON.parse(msg.content)
                  : msg.content;

                if (parsed.response) {
                  // Use refs for synchronous comparison — avoid async functional update race condition
                  if (currentConversationIdRef.current === streamingConversationId.current) {
                    setChatHistory(prev => [...prev, {
                      id: parsed.id || `temp-${Date.now()}`,
                      role: 'assistant',
                      content: [{ type: 'text', text: parsed.response }],
                      created_at: formatMessageTime(new Date().toISOString())
                    }]);
                  }
                }
              } catch (parseErr) {
                console.warn('Failed to parse done content:', parseErr);
              }
            }
            streamingConversationId.current = null;
            fetchConversations();
            cleanup();
            break;
          }

          case 'error':
            message.error(msg.data?.message || 'Failed to get AI response');
            cleanup();
            break;

          default:
            console.warn('Unknown SSE message type:', msg.type);
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
        cleanup();
      }
    };
    es.onerror = (e) => {
      if (e && e.data) {
        message.error(t('operator.ai_chat.error', 'Connection error. Please try again.'));
        console.log("e.data", e.data)
      }
      // setStreamingContent([]);
      setProcessing(false);
      // setCurrentJobId(null);
      es.close();
      eventSourceRef.current = null;
    };

    return () => es.close();
  }, [currentJobId]);

  // Create new conversation
  const handleNewChat = () => {
    setCurrentConversationId(null);
    setChatHistory([{
      role: 'assistant',
      content: [{ type: 'text', text: t('operator.ai_chat.welcome_message', 'Hello! I\'m your AI assistant. How can I help you today?') }],
      created_at: formatMessageTime(new Date().toISOString())
    }]);
    setInputValue('');
    setStreamingContent([]);
    setUploadedFile(null);
  };

  // Select existing conversation
  const handleSelectConversation = (conversation) => {
    setCurrentConversationId(conversation.id);
    fetchConversationMessages(conversation.id);
  };

  // Delete conversation
  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/ai/v1/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        message.success(t('common.message.deleted', 'Deleted successfully'));
        fetchConversations();

        if (currentConversationId === conversationId) {
          handleNewChat();
        }
      }
    } catch (error) {
      message.error(t('common.message.error', 'Operation failed'));
    }
  };

  // Handle send message
  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) {
      message.warning(t('operator.ai_chat.empty_input', 'Please enter a message'));
      return;
    }

    // Build content array for user message
    const contentArray = [{ type: 'text', text: trimmedInput }];

    // Add attachment to content if present
    if (uploadedFile) {
      if (uploadedFile.content_type?.startsWith('image/')) {
        contentArray.push({
          type: 'image_url',
          image_url: { url: uploadedFile.url }
        });
      } else {
        contentArray.push({
          type: 'file',
          file: {
            url: uploadedFile.url,
            filename: uploadedFile.filename,
            content_type: uploadedFile.content_type
          }
        });
      }
    }

    // Add user message to history
    const userMessage = {
      role: 'user',
      content: contentArray,
      created_at: formatMessageTime(new Date().toISOString())
    };

    setChatHistory(prev => [...prev, userMessage]);
    setInputValue('');
    setUploadedFile(null);
    setProcessing(true);
    setStreamingContent([]);

    try {
      const requestBody = {
        messages: trimmedInput,
        conversation_id: currentConversationId,
        context: {
          page_markdown: generatePageContextMarkdown(),
          current_page: window.location.pathname,
          locale: i18n?.language || 'zh-TW',
        },
      };

      // Include file info if attached
      if (uploadedFile) {
        requestBody.attachment = {
          url: uploadedFile.url,
          filename: uploadedFile.filename,
          content_type: uploadedFile.content_type,
          size: uploadedFile.size
        };
      }

      const response = await fetch('/api/ai/v1/assistant', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        const displayMessage = data?.errors?.display || t('operator.ai_chat.error', 'Failed to get AI response. Please try again.');
        throw new Error(displayMessage);
      }

      // Update conversation ID if new conversation was created
      const resolvedConversationId = data.data?.conversation_id || currentConversationId;
      if (data.data?.conversation_id && !currentConversationId) {
        setCurrentConversationId(data.data.conversation_id);
      }

      // If streaming, set job ID; otherwise add response directly
      if (data.data?.run_id) {
        streamingConversationId.current = resolvedConversationId;
        currentConversationIdRef.current = resolvedConversationId; // sync immediately for fast done events
        setActiveStreamConvId(resolvedConversationId);
        setCurrentRunId(data.data.run_id);
        setCurrentJobId(data.data.run_id);
      } else if (data.data?.response) {
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: [{ type: 'text', text: data.data.response }],
          created_at: formatMessageTime(new Date().toISOString())
        }]);
        setProcessing(false);
        fetchConversations();
      }

    } catch (error) {
      message.error(error.message);
      setStreamingContent([]);
      setProcessing(false);
    }
  };

  // Cancel the current run
  //
  // Why it's more than just "close SSE + DELETE":
  //   - streamingContent/streamingBuffer hold text that hasn't been promoted to
  //     chatHistory yet. If we just clear them, the user loses whatever they saw.
  //   - Python (Go AI) continues asynchronously after the DELETE: it stops
  //     generating, then calls POST /callbacks/finish with { cancelled: true,
  //     response: "...partial..." } which Rails persists to the message.
  //     That authoritative server copy is what we want to display.
  //
  // Strategy:
  //   1. Preserve whatever streamingBuffer has locally → push as a temp
  //      assistant message so the UI doesn't flash empty.
  //   2. Close SSE, clear streaming state, DELETE the run.
  //   3. After a short delay (enough for Python → /finish → DB save), refetch
  //      conversation messages. fetchConversationMessages replaces chatHistory
  //      entirely, so the temp entry is swapped for the server's persisted copy.
  const handleCancel = async () => {
    if (!currentRunId) return;

    const runIdToCancel = currentRunId;
    const convoIdAtCancel = currentConversationId;
    const partialText = streamingBuffer.current;

    // Close SSE first — stop receiving more chunks
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Preserve the partial response locally so the user sees no flicker.
    // Server refetch will replace this shortly with the authoritative version.
    if (partialText && convoIdAtCancel) {
      setChatHistory(prev => [...prev, {
        id: `cancelled-temp-${Date.now()}`,
        role: 'assistant',
        content: [{ type: 'text', text: partialText }],
        status: 'cancelled',
        created_at: formatMessageTime(new Date().toISOString())
      }]);
    }

    // Reset streaming UI state
    setProcessing(false);
    setStreamingContent([]);
    setActiveStreamConvId(null);
    streamingConversationId.current = null;
    streamingBuffer.current = '';

    try {
      await fetch(`/api/ai/v1/runs/${runIdToCancel}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
    } catch (error) {
      // Best-effort cancel — ignore network errors
    }

    setCurrentRunId(null);
    setCurrentJobId(null);

    // Give Python a moment to finish saving via /callbacks/finish, then pull
    // the authoritative message back. 1500ms is a pragmatic buffer; if the
    // round trip is slower in production, bump this up.
    if (convoIdAtCancel) {
      setTimeout(() => {
        if (currentConversationIdRef.current === convoIdAtCancel) {
          fetchConversationMessages(convoIdAtCancel);
        }
      }, 1500);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      message.error(t('operator.ai_chat.file_too_large', 'File size exceeds 20MB limit'));
      return false;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadToken = getCurrentMerchantTokenSnapshot();
      const response = await fetch('/api/ai/v1/assistant/upload', {
        method: 'POST',
        headers: uploadToken ? { 'Authorization': `Bearer ${uploadToken}` } : {},
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setUploadedFile({
        url: data.data.url,
        filename: data.data.filename,
        content_type: data.data.content_type,
        size: data.data.size,
        path: data.data.path
      });
      message.success(t('operator.ai_chat.file_uploaded', 'File uploaded successfully'));
    } catch (error) {
      console.error('File upload error:', error);
      message.error(t('operator.ai_chat.upload_failed', 'Failed to upload file'));
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload behavior
  };

  // Remove uploaded file
  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!processing && !uploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (processing || uploading) return;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const messageCount = chatHistory.filter(m => m.role === 'user' || m.id).length;

  return (
    <>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .conversation-item:hover .delete-btn {
          opacity: 1;
        }
        .conversation-item .delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }
        .conversation-item:hover {
          background-color: #ececea !important;
        }
        .conversation-item.active:hover {
          background-color: #e6e6e1 !important;
        }
        .ai-drawer-header .ant-drawer-header-title {
          flex-direction: row-reverse;
        }
      `}</style>
      <Drawer
        className="ai-drawer-header"
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Space size={2}>
              <Tooltip title={sidebarCollapsed ? t('common.button.expand', 'Expand') : t('common.button.collapse', 'Collapse')}>
                <Button
                  type="text"
                  icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  style={{ color: '#555' }}
                />
              </Tooltip>
              <Tooltip title={t('operator.ai_chat.new_chat', 'New Chat')}>
                <Button
                  type="text"
                  icon={<FormOutlined />}
                  onClick={handleNewChat}
                  style={{ color: '#555' }}
                />
              </Tooltip>
            </Space>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {messageCount > 0
                ? t('operator.ai_chat.messages_count', { count: messageCount, defaultValue: '{{count}} messages' })
                : ''}
            </Text>
            <Tooltip title={t('operator.menu.api_integration.ai_audit_logs', 'AI Audit Logs')}>
              <Button
                type="text"
                icon={<AuditOutlined />}
                onClick={() => { onClose(); navigate('/ai_audit_logs'); }}
                style={{ color: '#555' }}
              />
            </Tooltip>
          </div>
        }
        placement="right"
        width={720}
        onClose={onClose}
        open={visible}
        styles={{ body: { display: 'flex', padding: 0, height: '100%', background: '#fafaf8', overflow: 'hidden', minWidth: 0 } }}
      >
        {/* Left Sidebar */}
        <ConversationSidebar
          collapsed={sidebarCollapsed}
          conversations={conversations}
          loading={loadingConversations}
          currentConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onSettings={() => { onClose(); navigate('/ai_audit_logs'); }}
        />

        {/* Right Panel - Chat Area */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fafaf8', overflow: 'hidden' }}>
          <ChatHistory
            messages={chatHistory}
            streamingContent={activeStreamConvId === currentConversationId ? streamingContent : []}
            processing={processing && (activeStreamConvId === null || activeStreamConvId === currentConversationId)}
          />
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onCancel={handleCancel}
            processing={processing && (activeStreamConvId === null || activeStreamConvId === currentConversationId)}
            uploadedFile={uploadedFile}
            uploading={uploading}
            isDragging={isDragging}
            onFileUpload={handleFileUpload}
            onRemoveFile={handleRemoveFile}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        </div>
      </Drawer>
    </>
  );
}
